/**
 * Local AI capability service.
 * -------------------------------------------------------------------------
 * Scores whether a device can run the on-device Oracle model. The browser app
 * has no native model runtime, so it will normally report `online_only` here.
 * React Native/native builds should expose a runtime adapter on
 * `globalThis.__F1_LOCAL_AI_RUNTIME__` or pass one directly to the test calls.
 * Runtime contract: getDeviceInfo(), benchmarkModel(), generateAnswer(), deleteModels().
 */

export const LOCAL_AI_MODELS = {
  standard: {
    key: 'standard',
    label: 'Standard Local AI',
    modelId: 'litert-community/gemma-4-E2B-it-litert-lm',
    shortName: 'Gemma 4 E2B LiteRT-LM',
    modelFile: 'gemma-4-E2B-it.litertlm',
    modelSizeMb: 2583,
    licenseUrl: 'https://huggingface.co/litert-community/gemma-4-E2B-it-litert-lm',
    minFreeStorageMb: 4096,
    minMemoryHeadroomMb: 1536,
    minTokensPerSecond: 8,
    maxLoadMs: 20_000,
    maxTimeToFirstTokenMs: 6_000,
  },
  enhanced: {
    key: 'enhanced',
    label: 'Enhanced Local AI',
    modelId: 'litert-community/gemma-4-E4B-it-litert-lm',
    shortName: 'Gemma 4 E4B LiteRT-LM',
    modelFile: 'gemma-4-E4B-it.litertlm',
    modelSizeMb: 3654,
    licenseUrl: 'https://huggingface.co/litert-community/gemma-4-E4B-it-litert-lm',
    minFreeStorageMb: 8192,
    minMemoryHeadroomMb: 3072,
    minTokensPerSecond: 6,
    maxLoadMs: 30_000,
    maxTimeToFirstTokenMs: 8_000,
  },
};

export const LOCAL_AI_PREFERENCES = {
  auto: 'auto',
  onlineOnly: 'online_only',
  standard: 'standard_local_ai',
  enhanced: 'enhanced_local_ai',
};

export const LOCAL_AI_TIERS = {
  onlineOnly: 'online_only',
  standard: 'standard_local_ai',
  enhancedCandidate: 'enhanced_candidate',
  enhanced: 'enhanced_local_ai',
};

const CAPABILITY_STORAGE_KEY = 'f1central.localAi.capability.v4';
const PREFERENCE_STORAGE_KEY = 'f1central.localAi.preference.v1';

const BENCHMARK_PROMPT = {
  system:
    'You are Oracle, an F1 assistant. Answer only from the provided facts. Keep the answer to one short sentence.',
  user: 'Explain what DRS is.',
  facts: [
    'DRS means Drag Reduction System.',
    'DRS opens a rear-wing flap in designated zones to reduce drag and help overtaking.',
  ],
  maxOutputTokens: 32,
};

export function getLocalAiRuntime(explicitRuntime) {
  if (explicitRuntime) return explicitRuntime;
  if (typeof globalThis !== 'undefined') return globalThis.__F1_LOCAL_AI_RUNTIME__ ?? null;
  return null;
}

export function getCachedLocalAiCapability() {
  return readJson(CAPABILITY_STORAGE_KEY);
}

export function clearCachedLocalAiCapability() {
  removeStoredValue(CAPABILITY_STORAGE_KEY);
}

export function getLocalAiPreference() {
  return readStoredValue(PREFERENCE_STORAGE_KEY) || LOCAL_AI_PREFERENCES.auto;
}

export function setLocalAiPreference(preference) {
  const allowed = Object.values(LOCAL_AI_PREFERENCES);
  const next = allowed.includes(preference) ? preference : LOCAL_AI_PREFERENCES.auto;
  writeStoredValue(PREFERENCE_STORAGE_KEY, next);
  return next;
}

export async function runLocalAiCompatibilityCheck({ runtime, signal, testEnhanced = false } = {}) {
  const adapter = getLocalAiRuntime(runtime);
  const startedAt = Date.now();
  const base = buildBaseResult({ testedAt: new Date().toISOString() });

  if (!adapter || typeof adapter.benchmarkModel !== 'function') {
    return persistCapability({
      ...base,
      score: 0,
      tier: LOCAL_AI_TIERS.onlineOnly,
      label: 'Local AI unavailable',
      reason: 'No native local AI runtime is available in this build.',
      hardGate: 'runtime_unavailable',
      elapsedMs: Date.now() - startedAt,
    });
  }

  const deviceInfo = await readDeviceInfo(adapter, signal);
  const standardPreflight = runPreflight(deviceInfo, LOCAL_AI_MODELS.standard);
  if (!standardPreflight.pass) {
    return persistCapability({
      ...base,
      score: 0,
      tier: LOCAL_AI_TIERS.onlineOnly,
      label: 'Local AI unavailable',
      reason: standardPreflight.reason,
      hardGate: standardPreflight.hardGate,
      deviceInfo,
      elapsedMs: Date.now() - startedAt,
    });
  }

  const standardBenchmark = await runModelBenchmark(adapter, LOCAL_AI_MODELS.standard, signal);
  const standardScore = scoreLocalAiCapability({
    model: LOCAL_AI_MODELS.standard,
    metrics: standardBenchmark,
    deviceInfo,
  });
  const standardTier = classifyStandardScore(standardScore.score);

  if (standardScore.score < 60) {
    return persistCapability({
      ...base,
      score: standardScore.score,
      tier: LOCAL_AI_TIERS.onlineOnly,
      label: 'Local AI not recommended',
      reason: standardScore.reason || 'The standard local model was too slow on this device.',
      hardGate: standardScore.hardGate,
      deviceInfo,
      metrics: { standard: standardBenchmark },
      breakdown: { standard: standardScore.breakdown },
      elapsedMs: Date.now() - startedAt,
    });
  }

  const result = {
    ...base,
    score: standardScore.score,
    tier: standardTier,
    label: standardTier === LOCAL_AI_TIERS.enhancedCandidate
      ? 'Enhanced AI may be available'
      : 'Standard Local AI available',
    reason: standardTier === LOCAL_AI_TIERS.enhancedCandidate
      ? 'The standard model passed strongly enough to test enhanced local AI.'
      : 'The standard local model passed the compatibility benchmark.',
    recommendedPreference: LOCAL_AI_PREFERENCES.standard,
    availablePreferences: [LOCAL_AI_PREFERENCES.auto, LOCAL_AI_PREFERENCES.onlineOnly, LOCAL_AI_PREFERENCES.standard],
    deviceInfo,
    metrics: { standard: standardBenchmark },
    breakdown: { standard: standardScore.breakdown },
    standardModelDeleted: false,
    elapsedMs: Date.now() - startedAt,
  };

  if (!testEnhanced || standardTier !== LOCAL_AI_TIERS.enhancedCandidate) {
    return persistCapability(result);
  }

  const enhancedResult = await runEnhancedCompatibilityCheck({ runtime: adapter, signal, baseResult: result });
  return persistCapability(enhancedResult);
}

export async function runEnhancedCompatibilityCheck({ runtime, signal, baseResult } = {}) {
  const adapter = getLocalAiRuntime(runtime);
  const current = baseResult || getCachedLocalAiCapability() || buildBaseResult();

  if (!adapter || typeof adapter.benchmarkModel !== 'function') {
    return persistCapability({
      ...current,
      tier: current.tier || LOCAL_AI_TIERS.onlineOnly,
      reason: 'No native local AI runtime is available in this build.',
    });
  }

  const deviceInfo = current.deviceInfo || await readDeviceInfo(adapter, signal);
  const enhancedPreflight = runPreflight(deviceInfo, LOCAL_AI_MODELS.enhanced);
  if (!enhancedPreflight.pass) {
    return persistCapability({
      ...current,
      enhancedScore: 0,
      enhancedReason: enhancedPreflight.reason,
      enhancedHardGate: enhancedPreflight.hardGate,
      tier: LOCAL_AI_TIERS.enhancedCandidate,
      label: 'Standard Local AI available',
      reason: 'Enhanced AI did not pass preflight, but standard local AI remains available.',
      deviceInfo,
    });
  }

  const enhancedBenchmark = await runModelBenchmark(adapter, LOCAL_AI_MODELS.enhanced, signal);
  const enhancedScore = scoreLocalAiCapability({
    model: LOCAL_AI_MODELS.enhanced,
    metrics: enhancedBenchmark,
    deviceInfo,
  });

  if (enhancedScore.score < 70) {
    return persistCapability({
      ...current,
      enhancedScore: enhancedScore.score,
      enhancedReason: enhancedScore.reason || 'Enhanced local AI was too slow on this device.',
      enhancedHardGate: enhancedScore.hardGate,
      metrics: { ...current.metrics, enhanced: enhancedBenchmark },
      breakdown: { ...current.breakdown, enhanced: enhancedScore.breakdown },
      tier: LOCAL_AI_TIERS.enhancedCandidate,
      label: 'Standard Local AI available',
      reason: 'Enhanced AI did not pass, but standard local AI remains available.',
      deviceInfo,
    });
  }

  return persistCapability({
    ...current,
    enhancedScore: enhancedScore.score,
    score: Math.max(current.score || 0, enhancedScore.score),
    tier: LOCAL_AI_TIERS.enhanced,
    label: 'Enhanced Local AI available',
    reason: 'Both standard and enhanced local models passed compatibility checks.',
    recommendedPreference: LOCAL_AI_PREFERENCES.enhanced,
    availablePreferences: [
      LOCAL_AI_PREFERENCES.auto,
      LOCAL_AI_PREFERENCES.onlineOnly,
      LOCAL_AI_PREFERENCES.standard,
      LOCAL_AI_PREFERENCES.enhanced,
    ],
    metrics: { ...current.metrics, enhanced: enhancedBenchmark },
    breakdown: { ...current.breakdown, enhanced: enhancedScore.breakdown },
    deviceInfo,
  });
}

export async function deleteLocalAiModels({ runtime } = {}) {
  const adapter = getLocalAiRuntime(runtime);
  if (adapter && typeof adapter.deleteModels === 'function') {
    await adapter.deleteModels([LOCAL_AI_MODELS.standard.modelId, LOCAL_AI_MODELS.enhanced.modelId]);
  }
  clearCachedLocalAiCapability();
  setLocalAiPreference(LOCAL_AI_PREFERENCES.onlineOnly);
}

export async function deleteStandardLocalAiModel({ runtime } = {}) {
  const adapter = getLocalAiRuntime(runtime);
  if (adapter && typeof adapter.deleteModels === 'function') {
    await adapter.deleteModels([LOCAL_AI_MODELS.standard.modelId]);
  }

  const current = getCachedLocalAiCapability() || buildBaseResult();
  const enhancedAvailable = current.tier === LOCAL_AI_TIERS.enhanced;
  const next = persistCapability({
    ...current,
    standardModelDeleted: true,
    standardModelDeletedAt: new Date().toISOString(),
    recommendedPreference: enhancedAvailable
      ? LOCAL_AI_PREFERENCES.enhanced
      : current.recommendedPreference,
    availablePreferences: enhancedAvailable
      ? [LOCAL_AI_PREFERENCES.auto, LOCAL_AI_PREFERENCES.onlineOnly, LOCAL_AI_PREFERENCES.enhanced]
      : current.availablePreferences,
    reason: enhancedAvailable
      ? 'Enhanced Local AI remains available. The standard E2B model was removed from this device.'
      : current.reason,
  });

  if (enhancedAvailable) setLocalAiPreference(LOCAL_AI_PREFERENCES.enhanced);
  return next;
}

export function scoreLocalAiCapability({ model, metrics, deviceInfo = {} }) {
  if (!metrics?.loaded) return hardFail('model_did_not_load', 'The model could not be loaded.');
  if (metrics.crashed) return hardFail('model_crashed', 'The model crashed during the benchmark.');
  if (metrics.oom || metrics.memoryWarning === 'critical') return hardFail('memory_failure', 'The model used too much memory.');
  if (metrics.thermalWarning) return hardFail('thermal_warning', 'The device reported thermal pressure during the benchmark.');
  if (metrics.tokensPerSecond < 6) return hardFail('generation_too_slow', 'The model generated too slowly.');
  if (metrics.timeToFirstTokenMs > 15_000) return hardFail('first_token_too_slow', 'The first response token took too long.');

  const breakdown = {
    timeToFirstToken: scoreTimeToFirstToken(metrics.timeToFirstTokenMs, model.maxTimeToFirstTokenMs),
    tokensPerSecond: scoreTokensPerSecond(metrics.tokensPerSecond, model.minTokensPerSecond),
    loadTime: scoreLoadTime(metrics.loadMs, model.maxLoadMs),
    memory: scoreMemory(deviceInfo.memoryHeadroomMb, model.minMemoryHeadroomMb),
    storage: scoreStorage(deviceInfo.freeStorageMb, model.minFreeStorageMb),
    thermalBattery: scoreThermalBattery(deviceInfo, metrics),
  };

  const score = Math.round(
    breakdown.timeToFirstToken * 0.25 +
      breakdown.tokensPerSecond * 0.3 +
      breakdown.loadTime * 0.15 +
      breakdown.memory * 0.15 +
      breakdown.storage * 0.1 +
      breakdown.thermalBattery * 0.05
  );

  return {
    score: clamp(score, 0, 100),
    hardGate: null,
    reason: null,
    breakdown,
  };
}

export function classifyStandardScore(score) {
  if (score >= 80) return LOCAL_AI_TIERS.enhancedCandidate;
  if (score >= 60) return LOCAL_AI_TIERS.standard;
  return LOCAL_AI_TIERS.onlineOnly;
}

export function describeLocalAiPreference(preference) {
  switch (preference) {
    case LOCAL_AI_PREFERENCES.onlineOnly:
      return 'Online AI only';
    case LOCAL_AI_PREFERENCES.standard:
      return 'Standard Local AI';
    case LOCAL_AI_PREFERENCES.enhanced:
      return 'Enhanced Local AI';
    case LOCAL_AI_PREFERENCES.auto:
    default:
      return 'Auto';
  }
}

async function readDeviceInfo(adapter, signal) {
  const runtimeInfo = typeof adapter.getDeviceInfo === 'function'
    ? await adapter.getDeviceInfo({ signal })
    : {};

  const storageInfo = await estimateBrowserStorage();
  return {
    runtimeSupported: true,
    thermalState: 'nominal',
    batteryState: 'unknown',
    ...storageInfo,
    ...runtimeInfo,
  };
}

function runPreflight(deviceInfo, model) {
  if (deviceInfo.runtimeSupported === false) {
    return failPreflight('runtime_unsupported', 'The local AI runtime is not supported on this device.');
  }
  if (typeof deviceInfo.freeStorageMb === 'number' && deviceInfo.freeStorageMb < model.minFreeStorageMb) {
    return failPreflight('storage_too_low', `At least ${model.minFreeStorageMb} MB of free storage is required.`);
  }
  if (deviceInfo.thermalState === 'serious' || deviceInfo.thermalState === 'critical') {
    return failPreflight('thermal_state', 'The device is too hot to benchmark local AI right now.');
  }
  if (deviceInfo.batteryState === 'critical') {
    return failPreflight('battery_state', 'The battery is too low to benchmark local AI right now.');
  }
  return { pass: true, hardGate: null, reason: null };
}

async function runModelBenchmark(adapter, model, signal) {
  const startedAt = Date.now();
  try {
    const metrics = await adapter.benchmarkModel({
      modelId: model.modelId,
      prompt: BENCHMARK_PROMPT,
      maxOutputTokens: BENCHMARK_PROMPT.maxOutputTokens,
      signal,
    });
    return {
      modelId: model.modelId,
      loaded: true,
      loadMs: null,
      timeToFirstTokenMs: null,
      tokensPerSecond: null,
      outputTokens: null,
      generationMs: null,
      ...metrics,
      elapsedMs: Date.now() - startedAt,
    };
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    return {
      modelId: model.modelId,
      loaded: false,
      crashed: true,
      error: error?.message || 'Unknown benchmark failure',
      elapsedMs: Date.now() - startedAt,
    };
  }
}

async function estimateBrowserStorage() {
  if (typeof navigator === 'undefined' || !navigator.storage?.estimate) return {};
  try {
    const estimate = await navigator.storage.estimate();
    if (typeof estimate.quota !== 'number' || typeof estimate.usage !== 'number') return {};
    return {
      freeStorageMb: Math.max(0, Math.round((estimate.quota - estimate.usage) / 1024 / 1024)),
      storageQuotaMb: Math.round(estimate.quota / 1024 / 1024),
      storageUsageMb: Math.round(estimate.usage / 1024 / 1024),
    };
  } catch {
    return {};
  }
}

function scoreTimeToFirstToken(value, target) {
  if (typeof value !== 'number') return 50;
  if (value <= target * 0.5) return 100;
  if (value <= target) return 85;
  if (value <= target * 1.35) return 65;
  return 35;
}

function scoreTokensPerSecond(value, minimum) {
  if (typeof value !== 'number') return 50;
  if (value >= minimum * 2) return 100;
  if (value >= minimum * 1.5) return 90;
  if (value >= minimum) return 75;
  if (value >= minimum * 0.75) return 45;
  return 20;
}

function scoreLoadTime(value, target) {
  if (typeof value !== 'number') return 55;
  if (value <= target * 0.5) return 100;
  if (value <= target) return 80;
  if (value <= target * 1.5) return 50;
  return 25;
}

function scoreMemory(value, minimum) {
  if (typeof value !== 'number') return 60;
  if (value >= minimum * 2) return 100;
  if (value >= minimum * 1.4) return 85;
  if (value >= minimum) return 70;
  return 25;
}

function scoreStorage(value, minimum) {
  if (typeof value !== 'number') return 60;
  if (value >= minimum * 2) return 100;
  if (value >= minimum * 1.25) return 80;
  if (value >= minimum) return 65;
  return 20;
}

function scoreThermalBattery(deviceInfo, metrics) {
  if (metrics.thermalWarning || deviceInfo.thermalState === 'serious' || deviceInfo.thermalState === 'critical') return 0;
  if (deviceInfo.thermalState === 'fair' || deviceInfo.batteryState === 'low') return 65;
  return 100;
}

function hardFail(hardGate, reason) {
  return { score: 0, hardGate, reason, breakdown: {} };
}

function failPreflight(hardGate, reason) {
  return { pass: false, hardGate, reason };
}

function buildBaseResult({ testedAt = new Date().toISOString() } = {}) {
  return {
    schemaVersion: 1,
    testedAt,
    score: null,
    tier: LOCAL_AI_TIERS.onlineOnly,
    label: 'Not tested',
    reason: 'Local AI compatibility has not been checked yet.',
    recommendedPreference: LOCAL_AI_PREFERENCES.onlineOnly,
    availablePreferences: [LOCAL_AI_PREFERENCES.auto, LOCAL_AI_PREFERENCES.onlineOnly],
    standardModel: LOCAL_AI_MODELS.standard.modelId,
    enhancedModel: LOCAL_AI_MODELS.enhanced.modelId,
  };
}

function persistCapability(result) {
  writeJson(CAPABILITY_STORAGE_KEY, result);
  return result;
}

function readJson(key) {
  const value = readStoredValue(key);
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function writeJson(key, value) {
  writeStoredValue(key, JSON.stringify(value));
}

function readStoredValue(key) {
  if (typeof localStorage === 'undefined') return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStoredValue(key, value) {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage can be unavailable in private mode; compatibility will still work
    // for the current session through React state.
  }
}

function removeStoredValue(key) {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore storage errors.
  }
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}
